import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogFooter,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../@/components/ui/select";
import { Spinner } from "@/components/spinner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { toast } from "sonner";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";

const getUserId = (row) =>
  row?.user_id ?? row?.userId ?? row?.id ?? row?._id ?? null;

export default function AssignRolesBulkDialog({
  users = [],
  open,
  onOpenChange,
  onAssigned,
}) {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const { getToken } = useAuth();

  const selectedUserIds = useMemo(() => {
    const rows = Array.isArray(users) ? users : [];
    const ids = rows
      .map((u) => getUserId(u))
      .filter((id) => id !== null && id !== undefined && String(id).trim() !== "")
      .map((id) => String(id));
    return Array.from(new Set(ids));
  }, [users]);

  const selectedCount = selectedUserIds.length;

  const fetchRoles = async () => {
    setLoading(true);
    setRoles([]);
    setSelectedRole("");
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.get(api_endpoint + "/user/roles", reqConfig);
      setRoles(response.data?.roles || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to fetch roles. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchRoles();
    }
  }, [open]);

  const handleAssignRole = async () => {
    if (selectedCount < 2) {
      toast.error("Select at least 2 users to assign a role.");
      return;
    }

    if (!selectedRole) {
      toast.error("Please select a role to assign.");
      return;
    }

    setAssigning(true);
    try {
      const token = await getToken();
      if (!token) return;

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const roleIdNum = Number(selectedRole);
      const roleId = Number.isFinite(roleIdNum) ? roleIdNum : selectedRole;

      const payload = {
        assignments: selectedUserIds.map((userId) => ({
          user_id: userId,
          role_id: roleId,
        })),
      };

      const response = await axios.post(
        api_endpoint + `/user/users/roles/bulk`,
        payload,
        reqConfig
      );

      const assignedCount = Number(response.data?.assigned_count ?? 0) || 0;
      const alreadyAssignedCount =
        Number(response.data?.already_assigned_count ?? 0) || 0;

      toast.success(
        `Role assigned: ${assignedCount} new, ${alreadyAssignedCount} already assigned.`
      );

      onAssigned?.(response.data);
      onOpenChange(false);
    } catch (error) {
      console.error("Error bulk assigning role:", error);
      toast.error("Failed to assign role. Please try again.");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-(--neutral-900)">
            Assign Role
          </DialogTitle>
          <DialogDescription className="font-body text-(--neutral-700)">
            Assign a role to <span className="font-semibold">{selectedCount}</span> selected
            user{selectedCount === 1 ? "" : "s"}.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-4">
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id.toString()}>
                    {role.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline" disabled={assigning}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                variant=""
                onClick={handleAssignRole}
                disabled={assigning || !selectedRole || selectedCount < 2}
              >
                {assigning ? <ClipLoader size={18} color="#fff" /> : "Assign"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
